import { List, Typography } from 'antd';
import { ChatCircleIcon, PhoneCircleIcon } from 'assets';
import dayjs from 'dayjs';
import { useLazyContactHistories } from 'hooks/contacts';
import { messages } from 'messages';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import React, { useEffect } from 'react';
import { ContactHistoryDto } from 'types/dto/contacts';
import { CONTACT_TYPE } from 'utils';
import { CustomerCodes } from './CustomerLookupContactHistoryItem';
import { twMerge } from 'tailwind-merge';

type CustomerLookupContactHistoryItemHistoriesProps = {
  searchCriteria?: any;
  contactId?: number;
  onCustomerClick?: (customerCode: string) => void;
};

const CustomerLookupContactHistoryItemHistories: React.FC<CustomerLookupContactHistoryItemHistoriesProps> = ({
  contactId,
  searchCriteria,
  onCustomerClick
}) => {
  const { data, fetchData, handleLoadMore, hasMore, isFetching, isLoading } = useLazyContactHistories();

  useEffect(() => {
    if (contactId) {
      fetchData({
        contactId,
        name: searchCriteria?.customerName,
        phoneNumber: searchCriteria?.customerPhoneNumber,
        address: searchCriteria?.customerAddress,
        customerCode: searchCriteria?.customerCode,
        wardId: searchCriteria?.wardId,
        districtId: searchCriteria?.districtId,
        ticketId: searchCriteria?.customerRequestCode
      });
    }
  }, [contactId]);

  const renderItem = (item: ContactHistoryDto) => {
    return <ContactHistoriesItem item={item} onCustomerClick={onCustomerClick} />;
  };
  return (
    <List
      className='my-2'
      rowKey={(item) => `history-${contactId}-${item.contactHistoryId}`}
      dataSource={data}
      renderItem={renderItem}
      footer={
        hasMore || isLoading || isFetching ? (
          <div className='flex items-center'>
            {isLoading || isFetching ? (
              <div className='flex w-full items-center gap-4'>
                <div className='skeleton-active h-12 w-12 rounded-full'></div>
                <div className='h-12'>
                  <div className='skeleton-active mb-2 h-5 w-[250px] rounded-base' />
                  <div className='skeleton-active h-5 w-[100px] rounded-base' />
                </div>
              </div>
            ) : hasMore ? (
              <Typography.Link
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoadMore();
                }}
              >
                {messages.LoadMore}
              </Typography.Link>
            ) : null}
          </div>
        ) : null
      }
    />
  );
};

export const ContactHistoriesItem: React.FC<{
  item: ContactHistoryDto;
  onCustomerClick?: (customerCode: string) => void;
  className?: string;
}> = ({ item, onCustomerClick, className }) => {
  const icon = {
    [CONTACT_TYPE.CHAT]: <ChatCircleIcon className='h-12 w-12' />,
    [CONTACT_TYPE.CALL]: <PhoneCircleIcon className='h-12 w-12' />
  };
  const description = {
    [CONTACT_TYPE.CHAT]: (channelName: string) => customerLookupMessages.chatFrom(channelName),
    [CONTACT_TYPE.CALL]: () => customerLookupMessages.callFrom
  };

  return (
    <div className={twMerge('flex items-start gap-4 py-2', className)}>
      <div>{icon[item.contactType] ?? <div className='h-12 w-12 rounded-full border'></div>}</div>
      <div className='flex flex-col gap-2'>
        <span>
          {description[item.contactType]?.(item.channelName)} {messages.atTime}{' '}
          {item.contactAt
            ? `${dayjs(item.contactAt).format('HH:mm')}, ngày ${dayjs(item.contactAt).format('DD/MM/YYYY')}`
            : ''}
        </span>
        <CustomerCodes onCustomerClick={onCustomerClick} customerCodes={[item.customerCode].filter(Boolean)} />
      </div>
    </div>
  );
};

export default CustomerLookupContactHistoryItemHistories;
